import React from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import './CarePlusOrderCard.css';
import * as translator from '../../../../../utils/translator';
import { MainTracker } from '../../../../../PixelTrackers/MainTracker';
import MyAccountContentHeader from '../../MyAccountContentHeader/MyAccountContentHeader';
import CopyToClipboard from '../../../../../components/copyToClipboard/copyToClipboard';
import { toLocaleDateString } from '../../../../../utils/helpers';
import { statusAccent, PLAN_TYPE_LABEL } from '../../CarePlusOrderDetail/carePlusConstants';
import { calculateColorAndDescription } from '../../MyAccountOrderDisclaimer/MyAccountOrderDisclaimerPreProcess';

interface CarePlusOrderCardProps {
  order: MyAccountOrder;
  defaultImage?: string;
}

const CarePlusOrderCard: React.FC<CarePlusOrderCardProps> = ({ order, defaultImage }) => {
  const { country, language } = useOutletContext<any>();
  const navigate = useNavigate();
  const ctx = order.carePlusContext!;

  const accent = statusAccent(ctx.subscriptionStatus, ctx.hasPaymentIssue);
  const locale = translator.getLocaleByCountry(country);

  const image = order.cartViewProducts?.[0]?.imageSrc || defaultImage;
  const productTitle = order.cartViewProducts?.[0]?.titleView || '';
  const productVariant = order.cartViewProducts?.[0]?.quantityStr || '';

  const statusData = calculateColorAndDescription(order, language, 'my-orders');
  const deliveryStatusText = translator.translate(
    language,
    'my_account',
    order.status === 'sent_to_pharmacy' && order.orderType !== 'prescription_only'
      ? 'MyOrders/details/timelineStatus/sent_to_pharmacy'
      : `MyOrders/status/${order.status}`,
  );

  const statusKey = ctx.hasPaymentIssue
    ? 'CarePlus/orderCard/statusGracePeriod'
    : ctx.subscriptionStatus === 'paused'
      ? 'CarePlus/orderCard/statusPaused'
      : ctx.subscriptionStatus === 'cancel' || ctx.subscriptionStatus === 'cancelled'
        ? 'CarePlus/orderCard/statusCancelled'
        : ctx.subscriptionStatus === 'pending'
          ? 'CarePlus/orderCard/statusPending'
          : 'CarePlus/orderCard/status';
  const statusText = translator.translate(language, 'my_account', statusKey);
  const planPrefixText = translator.translate(language, 'my_account', 'CarePlus/orderCard/planPrefix');
  const deliveryCycleText = (translator.translate(language, 'my_account', 'CarePlus/orderCard/deliveryCycle') || '').replace(':n', String(ctx.cycleNumber));
  // Shipping is bundled into Care+ Plan (checkout "Versandkosten inklusive"); no separate delivery row.
  const priceRows = ctx.prices ? ([
    ['CarePlus/orderCard/medication', ctx.prices.drug],
    ['CarePlus/orderCard/planFee', ctx.prices.carePlusPlan],
    ['CarePlus/orderCard/discount', ctx.prices.discount],
  ] as const).filter(([, value]) => value != null) : [];
  const paidLabel = translator.translate(language, 'my_account', 'MyOrders/paid');
  const detailsLabel = translator.translate(language, 'my_account', 'MyOrders/cardDetails');

  const dateOptions = { year: 'numeric' as const, month: 'long' as const, day: 'numeric' as const };

  const handleCardClick = () => {
    MainTracker.track('click', 'Go to Treatment Plan');
    navigate(`/${country}/account/my-orders/${order.hashID}/details`);
  };

  return (
    <div
      className={`careplus-order-card careplus-order-card--${accent}`}
      onClick={handleCardClick}
    >
      <div className="careplus-order-card__header">
        <MyAccountContentHeader
          title={order.hashID || ''}
          titleFurnish={<CopyToClipboard textToCopy={order.hashID || ''} />}
          titleClass="careplus-order-card__order-id"
          description={deliveryStatusText}
          descriptionClass={`order-status order-status-description ${statusData.class}`}
        />
        <span className="careplus-order-card__date">
          {toLocaleDateString(order.createdAt || '', language, dateOptions)}
        </span>
      </div>

      <div className="careplus-order-card__status-row">
        <span className="careplus-order-card__status-text">{statusText}</span>
        <span className="careplus-order-card__status-dot" aria-hidden="true" />
        <span className="careplus-order-card__plan-label">
          {planPrefixText}
          <span className="careplus-order-card__plan-type">{ctx.isMonthlyPlan ? translator.translate(language, 'my_account', 'CarePlus/monthlyPlan/label') : PLAN_TYPE_LABEL[ctx.planType]}</span>
        </span>
        <span className="careplus-order-card__status-dot" aria-hidden="true" />
        <span className="careplus-order-card__cycle-inline">{deliveryCycleText}</span>
      </div>

      <div className="careplus-order-card__body">
        <div className="careplus-order-card__image-tile">
          {image && <img src={image} alt={productTitle || 'medication'} />}
        </div>
        <div className="careplus-order-card__product">
          <span className="careplus-order-card__product-title">{productTitle}</span>
          {productVariant ? <span className="careplus-order-card__product-variant">{productVariant}</span> : null}
        </div>
      </div>

      <div className="careplus-order-card__footer">
        <button
          type="button"
          className="careplus-order-card__details-link"
          onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
        >
          {detailsLabel}
        </button>

        <div className="careplus-order-card__price-stack">
          {priceRows.map(([labelKey, value]) => (
            <div key={labelKey} className="careplus-order-card__price-row">
              <span className="careplus-order-card__price-label">{translator.translate(language, 'my_account', labelKey)}</span>
              <span className="careplus-order-card__price-value">{translator.getPrice(value as number, locale)}</span>
            </div>
          ))}
          <div className="careplus-order-card__price-paid-row">
            <span className="careplus-order-card__paid-label">{paidLabel}:</span>
            <span className="careplus-order-card__paid-amount">{order.prices.total}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CarePlusOrderCard;
